import { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { RelativesMapData } from '../components/Map3Relatives';
import { GRID_CONFIG } from '../gridConfig';
import { ColumnInfo } from './layoutMath';
import { getMaxIntervals } from '../../utils/intervalUtils';

/**
 * Stacked view layout math.
 *
 * This is used to layout the stacked view in the master grid.
 *
 * In stacked view mode, each map has its own timeline with fixed-width columns.
 * All columns are the same width (GRID_CONFIG.cellWidth).
 *
 * Rows are all the same height. As a result Y offsets and related math is the same
 * for all rows, and for both stacked and sharedTimeline view modes.
 *
 * Returns an array of column metadata for the stacked view.
 * Each object contains width, start/end, master grid index, and interval type.
 *
 * In stacked view mode, all columns have the same width (GRID_CONFIG.cellWidth).
 */
export function getStackedColumnsInfo(
  intervals: Array<{ start: Date; end: Date }>,
  intervalType: MilestoneMapInterval,
): ColumnInfo[] {
  return intervals.map((interval, idx) => ({
    width: GRID_CONFIG.cellWidth,
    startDate: interval.start,
    endDate: interval.end,
    // In stacked view master and status columns indexes are the same.
    masterColumnIndex: idx,
    statusColumnIndex: idx,
    columnIntervalUnit: intervalType,
    // In stacked view, globalX is the absolute position in the grid
    globalX: GRID_CONFIG.objectiveWidth + idx * GRID_CONFIG.cellWidth,
    // In stacked view, localX is identical to globalX since the container isn't offset
    localX: GRID_CONFIG.objectiveWidth + idx * GRID_CONFIG.cellWidth,
  }));
}

/**
 * Returns the total width of the master ReactFlow grid in stacked view mode.
 * This is the sum of:
 * 1. The objective column width (fixed)
 * 2. The width of all columns in the map with the most intervals
 *
 * @param maps Array of maps containing intervals
 * @returns The total width of the master grid
 */
export function getTotalGridWidthStacked<
  T extends { intervals: Array<{ start: Date; end: Date }> },
>(maps: T[]): number {
  const maxIntervals = getMaxIntervals(maps);
  return GRID_CONFIG.objectiveWidth + maxIntervals * GRID_CONFIG.cellWidth;
}
